import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Search, MoreHorizontal, Coins, KeyRound, Trash2 } from "lucide-react";
import Pagination from "../components/Pagination";
import { TableSkeleton } from "../components/Skeletons";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function UsersPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  // Dialog state
  const [tokensModal, setTokensModal] = useState({ open: false, user: null, amount: "" });
  const [passwordModal, setPasswordModal] = useState({ open: false, user: null, password: "" });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [busy, setBusy] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (q) params.q = q;
      const res = await api.get("/admin/users", { params });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, q]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  useEffect(() => {
    setPage(1);
  }, [q]);

  const submitTokens = async () => {
    const amount = parseInt(tokensModal.amount, 10);
    if (!amount || Number.isNaN(amount)) {
      toast.error("Informe um número diferente de zero (positivo = adiciona, negativo = desconta)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${tokensModal.user.id}/tokens`, { amount });
      toast.success(`Saldo atualizado em ${amount > 0 ? "+" : ""}${amount} tokens.`);
      setTokensModal({ open: false, user: null, amount: "" });
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async () => {
    if ((passwordModal.password || "").length < 6) {
      toast.error("A senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${passwordModal.user.id}/password`, {
        password: passwordModal.password,
      });
      toast.success("Senha redefinida com sucesso.");
      setPasswordModal({ open: false, user: null, password: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleteModal.user.id}`);
      toast.success("Usuário excluído.");
      setDeleteModal({ open: false, user: null });
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 min-w-[200px] sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-md"
            data-testid="users-search"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="text-left bg-slate-50 border-b border-slate-200">
                {["Email", "Nome", "Tokens", "Criado em", "Último acesso", ""].map((h, i) => (
                  <th
                    key={i}
                    className="px-5 py-3 text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-0">
                    <TableSkeleton columns={6} rows={6} />
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-14 text-center text-slate-500" data-testid="users-empty">
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              ) : (
                data.items.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 font-medium text-slate-900 whitespace-nowrap">{u.email}</td>
                    <td className="px-5 py-3 text-slate-700">{u.name || "—"}</td>
                    <td className="px-5 py-3 text-slate-900 tabular-nums font-medium">{u.tokens ?? 0}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {u.created_at ? new Date(u.created_at).toLocaleString("pt-BR") : "—"}
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {u.last_login ? new Date(u.last_login).toLocaleString("pt-BR") : "Nunca"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            data-testid={`user-actions-${u.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem
                            onClick={() =>
                              setTokensModal({ open: true, user: u, amount: "" })
                            }
                            data-testid={`action-tokens-${u.id}`}
                          >
                            <Coins className="h-4 w-4 mr-2" />
                            Reabastecer tokens
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              setPasswordModal({ open: true, user: u, password: "" })
                            }
                            data-testid={`action-password-${u.id}`}
                          >
                            <KeyRound className="h-4 w-4 mr-2" />
                            Alterar senha
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteModal({ open: true, user: u })}
                            className="text-red-600 focus:text-red-700"
                            data-testid={`action-delete-${u.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir usuário
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && data.total > 0 && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            onChange={setPage}
          />
        )}
      </div>

      {/* Modal: Tokens */}
      <Dialog
        open={tokensModal.open}
        onOpenChange={(open) =>
          setTokensModal((m) => ({ ...m, open, ...(open ? {} : { amount: "" }) }))
        }
      >
        <DialogContent className="sm:max-w-md" data-testid="tokens-modal">
          <DialogHeader>
            <DialogTitle>Reabastecer tokens</DialogTitle>
            <DialogDescription>
              {tokensModal.user && (
                <>
                  Usuário: <span className="font-medium">{tokensModal.user.email}</span>
                  <br />
                  Saldo atual: <span className="font-medium tabular-nums">{tokensModal.user.tokens ?? 0}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="tokens-amount">Valor (positivo adiciona, negativo desconta)</Label>
            <Input
              id="tokens-amount"
              type="number"
              placeholder="ex.: 500"
              value={tokensModal.amount}
              onChange={(e) => setTokensModal((m) => ({ ...m, amount: e.target.value }))}
              data-testid="tokens-amount-input"
            />
            <div className="flex flex-wrap gap-2 pt-1">
              {[100, 500, 1000, 5000].map((v) => (
                <Button
                  key={v}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTokensModal((m) => ({ ...m, amount: String(v) }))}
                  data-testid={`tokens-preset-${v}`}
                >
                  +{v}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTokensModal({ open: false, user: null, amount: "" })}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button onClick={submitTokens} disabled={busy} data-testid="tokens-submit">
              {busy ? "Salvando…" : "Aplicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Senha */}
      <Dialog
        open={passwordModal.open}
        onOpenChange={(open) =>
          setPasswordModal((m) => ({ ...m, open, ...(open ? {} : { password: "" }) }))
        }
      >
        <DialogContent className="sm:max-w-md" data-testid="password-modal">
          <DialogHeader>
            <DialogTitle>Alterar senha</DialogTitle>
            <DialogDescription>
              {passwordModal.user && (
                <>
                  Defina uma nova senha para <span className="font-medium">{passwordModal.user.email}</span>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-password">Nova senha (mínimo 6 caracteres)</Label>
            <Input
              id="new-password"
              type="text"
              autoComplete="new-password"
              placeholder="Nova senha"
              value={passwordModal.password}
              onChange={(e) => setPasswordModal((m) => ({ ...m, password: e.target.value }))}
              data-testid="password-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPasswordModal({ open: false, user: null, password: "" })}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button onClick={submitPassword} disabled={busy} data-testid="password-submit">
              {busy ? "Salvando…" : "Salvar nova senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm: Delete */}
      <AlertDialog
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal((m) => ({ ...m, open }))}
      >
        <AlertDialogContent data-testid="delete-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente. O usuário{" "}
              <span className="font-medium">{deleteModal.user?.email}</span> será removido junto com
              todas as conversas de IA e logs vinculados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitDelete}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 focus-visible:ring-red-600"
              data-testid="delete-confirm"
            >
              {busy ? "Excluindo…" : "Excluir permanentemente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
